/** @jsxRuntime automatic @jsxImportSource react */ // [C35] the PDF worker loads these files through tsx outside its own tsconfig
// Extracted from original/src/components/CustomerReceiptVoucher.tsx:298-415 — the A4 customer entry voucher ("وصل قيد",
// the `customer-voucher-<id>` element the statement dialog's voucher window showed and `printElement` printed;
// reference documentation/01_visual_reference/print/p02_customer_entry_voucher-1.png). ADR-08 §2.1 "copy, then cut
// logic". Kept: every class string of the voucher (header with the office block, centre logo and the number / date /
// time / status block; the double rule; the subject badge "م/ …"; the salutation and covering line; the two-column
// table with its tinted amount and balance rows; the note and executing-box rows; the operator / signature footer).
// Cut: the hard-coded office fallback (name, phones, address) → `officeName` (D-052, D-125); the browser counter
// `AC-<movement id or random>` → the server's document number issued at the first print with the operator's employee
// code (D-147, D-272); every figure the old component recomputed from item texts and notes (`isCurrencyTrade`,
// `isPayment`, `remaining_amount`, a regex on "على الحساب:", the running balance re-walk :155-247) → the server's
// per-currency lines and subject code (F-273); `toLocaleDateString('ar-EG')` of the browser → `dateLabel` /
// `timeLabel` in the office time zone (D-064, D-162). Changed by decision: a reprint carries "نسخة / Copy" (D-147); an
// operation that moved both currency lines of the account (a conversion) shows one block of rows per currency; the
// typo "الالبطاقة" is written "البطاقة" (V-60). All strings are `print.entry.*` keys.
import { createT } from '@faylasuf/i18n';
import type { Locale } from '@faylasuf/i18n';

export interface CustomerEntryLine {
  /** "IQD" | "USD" — chooses the currency row text. */
  readonly currency: string;
  readonly beforeLabel: string;
  readonly amountLabel: string;
  readonly afterLabel: string;
}

export interface CustomerEntryVoucherA4Props {
  readonly locale: Locale;
  readonly id?: string;
  readonly officeName: string;
  readonly logoUrl?: string;
  /** `<employee code>-<document number>` (old "AC-7" shape). */
  readonly numberLabel: string;
  readonly copy: boolean;
  readonly dateLabel: string;
  readonly timeLabel: string;
  /** `print.entry.subject.<code>` / `print.entry.movement.<code>` (server subject code, D-272). */
  readonly subject: string;
  /** Debt decrease or trust increase: the amount row says "تسديد", otherwise "صرف/إضافة" (old `isPayment`). */
  readonly payment: boolean;
  readonly customerName: string;
  readonly accountNumber: string | null;
  readonly lines: readonly CustomerEntryLine[];
  readonly note: string | null;
  /** The box / vault the operation used (old `wallet_name`). */
  readonly boxLabel: string | null;
  readonly operatorCode: string;
  /** [C35] D-148 words per line (server); printed on the 80 mm copy only — the old A4 voucher had no words line. */
  readonly words?: readonly string[];
}

export function CustomerEntryVoucherA4(p: CustomerEntryVoucherA4Props) {
  const t = createT(p.locale);
  const two = p.lines.length > 1;
  return (
    <div
      id={p.id ?? 'customer-entry-voucher'}
      className="bg-white text-black p-8 max-w-[800px] mx-auto font-sans text-right leading-normal print:p-4 print:max-w-none"
      dir={p.locale === 'ar' ? 'rtl' : 'ltr'}
      data-testid="customer-entry-voucher"
    >
      {/* Header */}
      <div className="flex justify-between items-center pb-3">
        {/* Right Header */}
        <div className="text-right">
          <h1 className="text-lg font-black text-black tracking-tight">{p.officeName}</h1>
          <p className="text-[11px] font-bold text-gray-700 tracking-tight">{t('print.entry.platform_line')}</p>
          <p className="text-[10px] font-bold text-gray-800">{t('print.entry.department')}</p>
        </div>

        {/* Center Logo */}
        <div className="flex justify-center">{p.logoUrl ? <img src={p.logoUrl} alt="" className="h-14 w-auto object-contain" /> : null}</div>

        {/* Left Info */}
        <div className="text-left text-xs font-bold text-black space-y-1" dir="ltr">
          <p className="text-right">
            <span className="font-normal text-gray-700">{t('print.entry.number')}: </span>
            <span className="font-mono font-black" data-testid="entry-number">{p.numberLabel}</span>
          </p>
          <p className="text-right">
            <span className="font-normal text-gray-700">{t('print.entry.date')}: </span>
            <span className="font-mono">{p.dateLabel}</span>
          </p>
          <p className="text-right">
            <span className="font-normal text-gray-700">{t('print.entry.time')}: </span>
            <span className="font-mono">{p.timeLabel}</span>
          </p>
          <p className="text-right">
            <span className="font-normal text-gray-700">{t('print.entry.status')}: </span>
            <span className="font-bold text-emerald-700">{t('print.entry.status_posted')}</span>
          </p>
          {p.copy && (
            <p className="text-right">
              <span className="inline-block px-3 py-0.5 bg-rose-50 text-rose-700 font-black rounded-full text-[10px]" data-testid="entry-copy">
                {t('print.voucher.copy')}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* Double Divider Line */}
      <div className="border-t-2 border-b border-black h-1 my-2"></div>

      {/* Subject Badge */}
      <div className="my-6 text-center">
        <div className="inline-block border-2 border-black rounded-2xl bg-gray-100 text-black px-8 py-2 font-black text-sm shadow-sm">
          {t('print.entry.subject_prefix')} {t(`print.entry.subject.${p.subject}`)}
        </div>
      </div>

      {/* Salutation */}
      <div className="mb-6 space-y-2">
        <p className="font-black text-sm text-black">
          {t('print.entry.to')} <span className="font-black text-base mr-1 underline">{p.customerName}</span>
          {p.accountNumber && <span className="text-xs text-gray-600 font-mono mr-2">({t('print.entry.account_number')}: {p.accountNumber})</span>}
        </p>
        <p className="text-xs font-bold text-gray-800 leading-relaxed">{t('print.entry.covering')}</p>
      </div>

      {/* Main Table */}
      <table className="w-full border-collapse border border-black my-6 text-xs">
        <thead>
          <tr className="bg-gray-100 text-black font-black">
            <th className="border border-black p-3 text-center w-[40%]">{t('print.entry.col_item')}</th>
            <th className="border border-black p-3 text-center w-[60%]">{t('print.entry.col_value')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-black p-3 font-bold bg-gray-50/50 text-center">{t('print.entry.row_number')}</td>
            <td className="border border-black p-3 font-mono font-black text-center">{p.numberLabel}</td>
          </tr>
          <tr>
            <td className="border border-black p-3 font-bold bg-gray-50/50 text-center">{t('print.entry.row_movement')}</td>
            <td className="border border-black p-3 font-bold text-center">{t(`print.entry.movement.${p.subject}`)}</td>
          </tr>
          {p.lines.map((l) => (
            <LineRows key={l.currency} line={l} two={two} payment={p.payment} t={t} />
          ))}
          {p.note && (
            <tr>
              <td className="border border-black p-3 font-bold bg-gray-50/50 text-center">{t('print.entry.row_note')}</td>
              <td className="border border-black p-3 font-medium text-center">{p.note}</td>
            </tr>
          )}
          {p.boxLabel && (
            <tr>
              <td className="border border-black p-3 font-bold bg-gray-50/50 text-center">{t('print.entry.row_box')}</td>
              <td className="border border-black p-3 font-bold text-center">{p.boxLabel}</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Footer Signatures */}
      <div className="mt-14 flex justify-between items-start text-xs font-bold px-2">
        <div className="text-right space-y-1">
          <p className="font-black text-black text-sm">{t('print.entry.operator_title')}</p>
          <p className="text-gray-800">
            {t('print.entry.operator_code')}: <span className="font-mono font-black text-sm">{p.operatorCode}</span>
          </p>
          <p className="text-gray-600 text-[11px]">{t('print.entry.operator_department')}</p>
        </div>

        <div className="text-center space-y-8">
          <p className="font-black text-black text-sm">{t('print.entry.signature')}</p>
          <div className="border-b border-dashed border-black w-56 mx-auto"></div>
        </div>
      </div>
    </div>
  );
}

function LineRows({ line, two, payment, t }: { line: CustomerEntryLine; two: boolean; payment: boolean; t: (k: string, p?: Record<string, string | number>) => string }) {
  const suffix = two ? ` (${line.currency})` : '';
  return (
    <>
      <tr>
        <td className="border border-black p-3 font-bold bg-gray-50/50 text-center">{t('print.entry.row_currency')}</td>
        <td className="border border-black p-3 font-bold text-center">{line.currency === 'USD' ? t('print.entry.currency_usd') : t('print.entry.currency_iqd')}</td>
      </tr>
      <tr>
        <td className="border border-black p-3 font-bold bg-gray-50/50 text-center">{t('print.entry.row_before')}{suffix}</td>
        <td className="border border-black p-3 font-mono font-black text-center">{line.beforeLabel}</td>
      </tr>
      <tr className="bg-blue-50/30">
        <td className="border border-black p-3 font-black bg-blue-100/50 text-center text-blue-950">
          {t('print.entry.row_amount', { kind: payment ? t('print.entry.kind_payment') : t('print.entry.kind_add') })}
          {suffix}
        </td>
        <td className="border border-black p-3 font-mono font-black text-center text-base text-blue-900">{line.amountLabel}</td>
      </tr>
      <tr className="bg-emerald-50/30">
        <td className="border border-black p-3 font-black bg-emerald-100/50 text-center text-emerald-950">{t('print.entry.row_after')}{suffix}</td>
        <td className="border border-black p-3 font-mono font-black text-center text-base text-emerald-900">{line.afterLabel}</td>
      </tr>
    </>
  );
}
